import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Paper,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { PendingChannel } from '../../types/pendingChannels';
import { bulkApprovePendingChannels, BulkApprovalResult } from '../../services/firestoreChannelService';
import { modalStyles } from '../../utils/modalStyles';
import { useAuth } from '../../contexts/AuthContext';

interface BulkApprovePendingChannelsModalProps {
  open: boolean;
  channels: (PendingChannel & { _id: string })[];
  onClose: () => void;
  onSuccess: (result: BulkApprovalResult) => void;
  targetTables: string[];
}

interface ChannelValidation {
  channelId: string;
  name: string;
  email: string;
  isValid: boolean;
  errors: string[];
}

export const BulkApprovePendingChannelsModal: React.FC<BulkApprovePendingChannelsModalProps> = ({
  open,
  channels,
  onClose,
  onSuccess,
  targetTables,
}) => {
  const [targetTable, setTargetTable] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingError, setProcessingError] = useState<string | null>(null);
  const { currentUser } = useAuth();

  // Validate each channel
  const validatedChannels = useMemo(() => {
    return channels.map((channel) => {
      const errors: string[] = [];

      // Check email
      if (!channel.email || channel.email.trim() === '') {
        errors.push('Missing email');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(channel.email)) {
        errors.push('Invalid email format');
      }

      // Check contact method
      const hasContactMethod =
        (channel.email && channel.email.trim() !== '') ||
        (channel.socialLinks?.twitter && channel.socialLinks.twitter.trim() !== '') ||
        (channel.socialLinks?.instagram && channel.socialLinks.instagram.trim() !== '') ||
        (channel.socialLinks?.website && channel.socialLinks.website.trim() !== '');

      if (!hasContactMethod) {
        errors.push('No contact method');
      }

      return {
        channelId: channel._id,
        name: channel.title || '-',
        email: channel.email || '-',
        isValid: errors.length === 0,
        errors,
      };
    });
  }, [channels]);

  // Count stats
  const validCount = useMemo(() => validatedChannels.filter((c) => c.isValid).length, [validatedChannels]);
  const invalidCount = useMemo(() => validatedChannels.filter((c) => !c.isValid).length, [validatedChannels]);

  // Handle approval
  const handleApprove = async () => {
    if (!currentUser || !targetTable) {
      setProcessingError('Please select a target table and ensure you are logged in');
      return;
    }

    setIsProcessing(true);
    setProcessingError(null);

    try {
      // Only approve valid channels
      const validChannelIds = validatedChannels.filter((c) => c.isValid).map((c) => c.channelId);

      if (validChannelIds.length === 0) {
        setProcessingError('No valid channels to approve');
        setIsProcessing(false);
        return;
      }

      const result = await bulkApprovePendingChannels(
        validChannelIds,
        targetTable as 'youtube_reaction' | 'youtube_playlisting',
        currentUser.email || currentUser.uid
      );

      onSuccess(result);
      onClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to approve channels';
      setProcessingError(errorMessage);
      console.error('Error approving channels:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Determine if we can proceed
  const canApprove = targetTable && validCount > 0 && !isProcessing;

  return (
    <Dialog
      open={open}
      onClose={isProcessing ? undefined : onClose}
      maxWidth="lg"
      fullWidth
      sx={modalStyles.dialog}
    >
      <DialogTitle sx={modalStyles.dialogTitle}>
        Bulk Approve Channels ({channels.length})
      </DialogTitle>

      <DialogContent sx={modalStyles.dialogContent}>
        <Stack spacing={3}>
          {/* Summary Section */}
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
            <Stack direction="row" spacing={3}>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Total Selected
                </Typography>
                <Typography variant="h6">{channels.length}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="success.main">
                  ✓ Valid
                </Typography>
                <Typography variant="h6" color="success.main">
                  {validCount}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="error.main">
                  ✗ Issues
                </Typography>
                <Typography variant="h6" color="error.main">
                  {invalidCount}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {/* Target Table Selection */}
          <FormControl fullWidth disabled={isProcessing}>
            <InputLabel>Target Table</InputLabel>
            <Select
              value={targetTable}
              onChange={(e) => setTargetTable(e.target.value)}
              label="Target Table"
            >
              <MenuItem value="">-- Select Target Table --</MenuItem>
              {targetTables.map((table) => (
                <MenuItem key={table} value={table}>
                  {table}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Validation Results */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Channel Validation Results
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell>Status</TableCell>
                    <TableCell>Channel Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Issues</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {validatedChannels.map((validated, index) => (
                    <TableRow
                      key={validated.channelId}
                      sx={{
                        bgcolor: validated.isValid ? 'success.lighter' : 'error.lighter',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <TableCell align="center">
                        {validated.isValid ? (
                          <CheckCircleIcon sx={{ color: 'success.main', fontSize: '1.2rem' }} />
                        ) : (
                          <WarningIcon sx={{ color: 'error.main', fontSize: '1.2rem' }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{validated.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          {validated.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {validated.errors.length > 0 ? (
                          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
                            {validated.errors.map((error, idx) => (
                              <Chip key={idx} label={error} size="small" color="error" variant="outlined" />
                            ))}
                          </Stack>
                        ) : (
                          <Chip label="Valid" size="small" color="success" variant="outlined" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Warning if there are invalid channels */}
          {invalidCount > 0 && (
            <Alert severity="warning">
              {invalidCount} channel(s) have validation issues and will not be approved. Only{' '}
              <strong>{validCount}</strong> valid channel(s) will be processed.
            </Alert>
          )}

          {/* Error Alert */}
          {processingError && <Alert severity="error">{processingError}</Alert>}

          {/* Processing Info */}
          {isProcessing && (
            <Alert severity="info" icon={<CircularProgress size={20} />}>
              Processing bulk approval... This may take a moment.
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={modalStyles.dialogActions}>
        <Button onClick={onClose} disabled={isProcessing}>
          Cancel
        </Button>
        <Button
          onClick={handleApprove}
          disabled={!canApprove}
          variant="contained"
          color="success"
        >
          {isProcessing ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Processing...
            </>
          ) : (
            `Approve ${validCount} Channel${validCount !== 1 ? 's' : ''}`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkApprovePendingChannelsModal;
